/**
 * The checks on a preventivo before it becomes an offer (rules of the
 * organisation, allegato B §3, §4 and §5, 03/10/2026). Pure: the API works
 * them out from the lines, their costs and the parameters; the web app shows
 * them as they come.
 *
 * - Formazione, coaching, consulenza and the other voices: margin on the price
 *   (consulenza and «Altro» since 04/10/2026). From the green threshold up
 *   autonomy, from the yellow one the CFO is informed, below it the CFO must
 *   validate.
 * - Licenze e sviluppo: discount on the list price. Up to the green threshold
 *   autonomy, up to the yellow one the CFO is informed, beyond it validation.
 * - Mixed offers: every line with its own rule, the most restrictive outcome
 *   for the whole offer.
 * - Payment terms other than the standard: always the CFO's validation.
 * - From the amount threshold up the AD approves (in every version).
 * - Complex contract: only a warning on the offer.
 */
import { z } from 'zod';
import { formatDecimal, formatMoney } from './money.js';
import { optionalText } from './validation.js';
export const CHECK_OUTCOME_LABELS = {
    autonomia: 'Autonomia',
    informato: 'CFO informato',
    convalida: 'Convalida del CFO',
};
const RANK = { autonomia: 0, informato: 1, convalida: 2 };
/** The most restrictive of the outcomes; autonomy without any. */
export function worstOutcome(outcomes) {
    return outcomes.reduce((worst, outcome) => (outcome && RANK[outcome] > RANK[worst] ? outcome : worst), 'autonomia');
}
/** Which rule a type of line follows (column «Controllo» of Tipologie di riga). */
export const LINE_CONTROLS = ['Margine', 'Sconto su listino'];
export const DEFAULT_OFFER_PARAMETERS = {
    adThreshold: 50000,
    standardTermsCode: null,
    validityDays: 30,
    complexMonths: 12,
    adminContactEmail: null,
};
/** Codes of the voices of the table Parametri. */
export const OFFER_PARAMETER_CODES = {
    adThreshold: 'SOGLIA-AD',
    standardTermsCode: 'PAGAMENTO-STD',
    validityDays: 'VALIDITA',
    complexMonths: 'DURATA-COMPLESSO',
    adminContactEmail: 'RIF-AMMINISTRAZIONE',
};
/** The parameters from the voices of the table, the defaults where a value is missing or wrong. */
export function offerParametersFrom(entries) {
    const raw = (code) => {
        const value = entries.find((entry) => entry.code === code)?.values.value;
        return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    };
    const positive = (code, fallback) => {
        const number = Number(raw(code).replace(/\./g, '').replace(',', '.'));
        return Number.isFinite(number) && number > 0 ? number : fallback;
    };
    return {
        adThreshold: positive(OFFER_PARAMETER_CODES.adThreshold, DEFAULT_OFFER_PARAMETERS.adThreshold),
        standardTermsCode: raw(OFFER_PARAMETER_CODES.standardTermsCode).toUpperCase() || null,
        validityDays: positive(OFFER_PARAMETER_CODES.validityDays, DEFAULT_OFFER_PARAMETERS.validityDays),
        complexMonths: positive(OFFER_PARAMETER_CODES.complexMonths, DEFAULT_OFFER_PARAMETERS.complexMonths),
        adminContactEmail: raw(OFFER_PARAMETER_CODES.adminContactEmail).toLowerCase() || null,
    };
}
/** Margin on the price, in %: (sold − cost) ÷ sold. */
export function marginPercent(sold, cost) {
    if (sold <= 0)
        return cost > 0 ? -100 : 0;
    return ((sold - cost) / sold) * 100;
}
function marginOutcome(percent, thresholds) {
    if (percent >= thresholds.green)
        return 'autonomia';
    if (percent >= thresholds.yellow)
        return 'informato';
    return 'convalida';
}
function discountOutcome(percent, thresholds) {
    if (percent <= thresholds.green)
        return 'autonomia';
    if (percent <= thresholds.yellow)
        return 'informato';
    return 'convalida';
}
/** Whole months between two dates, counting a started month. */
export function monthsBetween(start, end) {
    const [y1, m1, d1] = start.split('-').map(Number);
    const [y2, m2, d2] = end.split('-').map(Number);
    return (y2 - y1) * 12 + (m2 - m1) + (d2 >= d1 ? 1 : 0);
}
const pct = (value) => `${formatDecimal(value, 0, 1)}%`;
const eur = (value) => formatMoney(value);
export function quoteChecks(input) {
    const checks = [];
    const { margin, discount, parameters } = input;
    // Formazione e coaching: margin on the price, line by line.
    const marginLines = input.lines.filter((line) => line.control === 'Margine' && !line.expense);
    if (marginLines.length > 0) {
        const rows = marginLines.map((line) => {
            const value = marginPercent(line.sold, line.cost ?? 0);
            return { label: line.label, value, outcome: marginOutcome(value, margin) };
        });
        const worst = rows.reduce((low, row) => (row.value < low.value ? row : low));
        checks.push({
            key: 'margin',
            label: 'Margine',
            detail: rows.length === 1
                ? `${pct(worst.value)} (autonomia da ${pct(margin.green)}, convalida sotto ${pct(margin.yellow)})`
                : `il più basso ${pct(worst.value)}, «${worst.label}» (autonomia da ${pct(margin.green)}, convalida sotto ${pct(margin.yellow)})`,
            outcome: worstOutcome(rows.map((row) => row.outcome)),
            lines: rows,
        });
    }
    // Licenze e sviluppo: discount on the list price, line by line.
    const discountLines = input.lines.filter((line) => line.control === 'Sconto su listino');
    if (discountLines.length > 0) {
        const listed = discountLines.filter((line) => line.discount !== null);
        const rows = listed.map((line) => ({
            label: line.label,
            value: line.discount,
            outcome: discountOutcome(line.discount, discount),
        }));
        const unlisted = discountLines.length - listed.length;
        const worst = rows.length > 0 ? rows.reduce((high, row) => (row.value > high.value ? row : high)) : null;
        checks.push({
            key: 'discount',
            label: 'Sconto su listino licenze e sviluppo',
            detail: [
                worst
                    ? worst.value <= 0
                        ? 'a listino'
                        : `sconto massimo ${pct(worst.value)}${rows.length > 1 ? `, «${worst.label}»` : ''} (oltre ${pct(discount.yellow)} convalida)`
                    : null,
                unlisted > 0
                    ? `${unlisted === 1 ? '1 riga senza' : `${unlisted} righe senza`} prezzo di listino`
                    : null,
            ]
                .filter(Boolean)
                .join(' · '),
            outcome: rows.length > 0 ? worstOutcome(rows.map((row) => row.outcome)) : null,
            lines: rows,
        });
    }
    // The spese (2.53): out of the margin, their costs within what the client pays for them.
    const expenses = input.expenses;
    if (expenses && (expenses.active !== 0 || expenses.passive !== 0)) {
        const within = expenses.passive <= expenses.active;
        checks.push({
            key: 'expenses',
            label: 'Spese',
            detail: `costi ${eur(expenses.passive)} su ${eur(expenses.active)} addebitati al cliente${within ? '' : ': le spese costano più di quanto si addebita'}`,
            outcome: within ? 'autonomia' : 'convalida',
            lines: [],
        });
    }
    // Payment terms: anything but the standard needs the CFO, at any amount.
    const standard = parameters.standardTermsCode;
    const other = (code) => code !== null && code !== undefined && standard !== null && code !== standard;
    // An instalment with its own non-standard terms counts as the offer's (05/10/2026).
    const otherTerms = other(input.paymentTermsCode) || (input.installmentTerms ?? []).some((code) => other(code));
    checks.push({
        key: 'payment',
        label: 'Tempi di incasso',
        detail: otherTerms
            ? 'termine diverso dallo standard'
            : standard === null
                ? 'termine standard non impostato nei Parametri'
                : 'termine standard',
        outcome: otherTerms ? 'convalida' : 'autonomia',
        lines: [],
    });
    // The amount: from the threshold up the AD approves.
    const needsAdApproval = input.net >= parameters.adThreshold;
    checks.push({
        key: 'amount',
        label: 'Importo',
        detail: needsAdApproval
            ? `${eur(input.net)}: da ${eur(parameters.adThreshold)} approva l’AD`
            : `${eur(input.net)}: sotto ${eur(parameters.adThreshold)} approva il commerciale`,
        outcome: null,
        lines: [],
    });
    // Complex contract: a warning only, the block is at the signature.
    const complexReasons = [];
    if (input.penalties)
        complexReasons.push('penali o clausole non standard');
    if (input.startDate &&
        input.endDate &&
        monthsBetween(input.startDate, input.endDate) > parameters.complexMonths) {
        complexReasons.push(`durata oltre ${parameters.complexMonths} mesi`);
    }
    if (input.largeClient && input.firstContract) {
        complexReasons.push('primo contratto con un cliente da 1.000 dipendenti o più');
    }
    checks.push({
        key: 'complex',
        label: 'Contratto complesso',
        detail: complexReasons.length > 0
            ? `sì: ${complexReasons.join('; ')}. Prima della firma serve il parere di un consulente`
            : 'no',
        outcome: null,
        lines: [],
    });
    const outcome = worstOutcome(checks.map((check) => check.outcome));
    return {
        checks,
        outcome,
        needsValidation: outcome === 'convalida',
        needsAdApproval,
        complexReasons,
    };
}
/* --- Validation by the CFO ----------------------------------------------------- */
/** Body of `POST /api/quotes/:id/validation`: the CFO validates or refuses, with a note. */
export const validationDecisionSchema = z
    .object({
    outcome: z.enum(['granted', 'refused']),
    note: optionalText(500),
})
    .refine((body) => body.outcome === 'granted' || body.note, {
    message: 'Scrivi perché la convalida è rifiutata',
    path: ['note'],
});
//# sourceMappingURL=offer-checks.js.map